import { DownloadOutlined } from '@ant-design/icons'
import { Button, Card, Checkbox, Form, Select, Space } from 'antd'
import fileSaver from 'file-saver'
import { Helmet } from 'react-helmet-async'
import { ExportRoutesFormat, SalesGroupRoute } from '@wolf/entities'
import { usePageOutletContext } from '../../../page-outlet-context'
import { dayNames } from '../../../utils/day-names'
import { trpc } from '../../../utils/trpc'

const ROUTE_NAME: Record<number, Array<string>> = {
  1: ['Seur', 'GLS', 'E01C'], // By the moment we don't work with Correos. Add it if needed
  2: ['DPD', 'DHL'],
  3: ['Seur']
}

export const ExportAlternativeRoutes: React.FC = () => {
  const { salesGroupFilter, sourceFilter, workspace } = usePageOutletContext()
  const routeNames = ROUTE_NAME[sourceFilter.serialId]

  const salesGroupsRoutes = trpc.salesGroups.getSalesGroupsRoutes.useQuery(
    {
      salesGroupId: salesGroupFilter.salesGroupId,
      sourceId: sourceFilter.serialId,
      routeNames
    },
    { enabled: salesGroupFilter.salesGroupId > -1, refetchOnWindowFocus: false }
  ).data

  const generateAlternativeRouteFileMutation = trpc.logistics.exportAlternativeRoute.useMutation({
    onSuccess: (data) => {
      const mediaType = 'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,'

      const file = `${mediaType}${data.buffer}`

      fileSaver.saveAs(file, data.name)
    }
  })

  const onFinish = async (values: { route: Array<string>; filterEmptyBoxes: boolean; format: ExportRoutesFormat }) => {
    const parsedRoutes = values.route.map(routeStr => JSON.parse(routeStr))

    generateAlternativeRouteFileMutation.mutate({
      sourceId: sourceFilter.serialId,
      workspaceId: workspace.serialId,
      salesGroupId: salesGroupFilter.salesGroupId,
      routes: parsedRoutes,
      format: values.format,
      ...(values.filterEmptyBoxes && { pickingState: 'EMPTY' }),
    })
  }

  if (!routeNames) return null

  const formattedRoutesNames = routeNames.length > 2 ? routeNames.slice(0, -1).join(', ') + ' y ' + routeNames[routeNames.length - 1] : routeNames.join(' y ')

  return (
    <>
      <Helmet title={`Exportar rutas ${formattedRoutesNames} - Wolf`} />

      <Card title={`Exportar rutas ${formattedRoutesNames}`} className="mb-4">
        <Form layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="route"
            label="Rutas"
            rules={[
              { required: true, message: 'Por favor introduce una ruta' },
            ]}
          >
            <Select
              style={{ minWidth: '12rem', width: 'auto' }}
              showSearch
              allowClear
              options={salesGroupsRoutes && salesGroupsRoutes.map((route: SalesGroupRoute) => (
                { label: `${route.route} - ${dayNames[route.day]}`, value: JSON.stringify(route) }
              ))}
              filterSort={(optionA, optionB) =>
                (optionA?.label ?? '').toLowerCase().localeCompare((optionB?.label ?? '').toLowerCase())
              }
              mode='multiple'
            />
          </Form.Item>
          <Form.Item
            name="format"
            label="Formato"
            rules={[
              { required: true, message: 'Por favor selecciona un formato' },
            ]}
          >
            <Select
              style={{ width: '12rem' }}
              allowClear
              options={Object.values(ROUTE_NAME[sourceFilter.serialId])
                // E01C will be always exported with Seur format
                .filter(routeName => routeName !== 'E01C')
                .map(routeName => (
                  { label: routeName, value: routeName }
                ))}
              filterSort={(optionA, optionB) =>
                (optionA?.label ?? '').toLowerCase().localeCompare((optionB?.label ?? '').toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item name="filterEmptyBoxes" valuePropName="checked">
            <Checkbox>Filtrar por cajas vacías</Checkbox>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                className="self-center mb-4 ml-auto"
                type="primary"
                htmlType="submit"
                icon={<DownloadOutlined />}
                loading={generateAlternativeRouteFileMutation.isLoading}
              >
                Exportar rutas
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </>
  )
}
